import React, { useCallback, useMemo, useState } from 'react';
import { Divider } from '@mui/material';

import { fetchContacts } from '@dh/api/contacts/fetchContacts';
import { fetchFaqs } from '@dh/api/faqs/fetchFaqs';
import { CollapsibleTile, ContactInfoUser, DigitalhubDivider } from '@dh/components';
import { useBreakpoint, useInitialize } from '@dh/hooks';
import { Column, TitleLarge, TitleMedium } from '@dh/styles';
import { Contact, Faq, FaqType, isCompact, User } from '@dh/types';
import NotFound from '@dh/app/[locale]/(digitalHub)/not-found';

import { FaqScreenGrid } from '../contact-and-faq/styles';
import { Container, LeftContainer, RightContainer } from '../contact/styles';

const FAQScreen = () => {
    const [faqs, setFaqs] = useState<Faq[]>([]);
    const [contactPersons, setContactPersons] = useState<Contact[]>([]);
    const [openedFaq, setOpenFaq] = useState<string | null>(faqs.length > 0 ? faqs[0].id : null);
    const [hasError, setHasError] = useState<boolean>(false);

    const breakpoint = useBreakpoint();

    const isCompactScreen = isCompact(breakpoint);

    useInitialize(() => {
        fetchFaqs()
            .then((response) => {
                setFaqs(response.faqs);
                setHasError(response.error !== undefined);
            })
            .catch((e) => {
                console.error(e);
                setHasError(true);
            });
        fetchContacts()
            .then((response) => {
                setContactPersons(response.contacts);
                setHasError(response.error !== undefined);
            })
            .catch((e) => {
                console.error(e);
                setHasError(true);
            });
    });

    const { general, administration, company } = useMemo(
        () =>
            faqs.reduce(
                (acc, faq) => {
                    switch (faq.type) {
                        case FaqType.General:
                            acc.general.push(faq);
                            break;
                        case FaqType.Administration:
                            acc.administration.push(faq);
                            break;
                        case FaqType.Association:
                            acc.administration.push(faq);
                            break;
                        case FaqType.Company:
                            acc.company.push(faq);
                            break;
                    }
                    return acc;
                },
                {
                    general: [] as Faq[],
                    administration: [] as Faq[],
                    company: [] as Faq[]
                }
            ),
        [faqs]
    );
    
    const renderTile = useCallback(
        (faq: Faq) => {
            return (
                <React.Fragment key={faq.id}>
                    <CollapsibleTile
                        title={faq.title}
                        description={faq.description}
                        isExpanded={openedFaq === faq.id}
                        toggleElement={() =>
                            openedFaq === faq.id ? setOpenFaq(null) : setOpenFaq(faq.id)
                        }
                        withMarkdown
                        hasSharpCorners={isCompactScreen}
                        sx={{ width: '100%' }}
                    />
                    <Divider />
                </React.Fragment>
            );
        },
        [isCompactScreen, openedFaq]
    );

    if (hasError) return <NotFound />;

    return (
        <Container viewSize={breakpoint}>
            <LeftContainer viewSize={breakpoint}>
                <FaqScreenGrid breakpoint={breakpoint} pb={4} px={isCompactScreen ? 0 : 3}>
                    <Column gap={5}>
                        {general.length > 0 && (
                            <Column gap={0}>
                                <TitleLarge>Allgemein</TitleLarge>
                                {general.map((faq) => renderTile(faq))}
                            </Column>
                        )}
                        {company.length > 0 && (
                            <Column gap={0}>
                                <TitleLarge>Unternehmen</TitleLarge>
                                {company.map((faq) => renderTile(faq))}
                            </Column>
                        )}
                        {administration.length > 0 && (
                            <Column gap={0}>
                                <TitleLarge>Verein und Verwaltung</TitleLarge>
                                {administration.map((faq) => renderTile(faq))}
                            </Column>
                        )}
                    </Column>
                </FaqScreenGrid>
            </LeftContainer>
            <DigitalhubDivider orientation='vertical' />
            <RightContainer viewSize={breakpoint}>
                <Column>
                    <TitleMedium>
                        Fragen, die nicht beantwortet wurden? Melden Sie sich bei uns!
                    </TitleMedium>
                    <Column gap={2} mt={2}>
                        {contactPersons.map((contact) => (
                            <ContactInfoUser
                                key={contact.id}
                                user={contact as Partial<User>}
                                personal={false}
                            />
                        ))}
                    </Column>
                </Column>
            </RightContainer>
        </Container>
    );
};

export default FAQScreen;
